import React, { useCallback, useMemo, useState } from "react";
import { ArrayField, useFieldArray } from "react-hook-form";
import { cloneDeep, get } from "lodash";
import { TemplateSection } from "../../graphql/types";
import useTranslations from "../../hooks/useTranslations";
import { FormStepProps } from "../../screens/MadFormScreen";
import { DeleteIcon, PlusIcon } from "../icons";
import { Button, Input, Textarea } from "../wombat-ui";
import { TemplateFormElementModal } from "./TemplateFormElementModal";
import TemplateQuestionForm, {
  TemplateQuestionFormInput,
} from "./TemplateQuestionForm";

export type TemplateSectionFormInput = Omit<
  TemplateSection,
  "id" | "__typename"
>;

export const TemplateSectionForm: React.FC<
  Omit<FormStepProps, "onNext"> & {
    defaultValue: Partial<ArrayField<TemplateSectionFormInput, "id">>;
    index: number;
    onRemove: (index: number) => void;
    handleFocus: (sectionIdx: number, questionIdx: number) => void;
    errors;
    focusedIdx: string;
    sectionDisplayIndex: number;
    shouldSelectReferent: boolean;
    name: string;
  }
> = React.memo(
  ({
    sectionDisplayIndex,
    register,
    unregister,
    setValue,
    getValues,
    index,
    control,
    errors,
    defaultValue,
    onRemove,
    shouldSelectReferent,
    handleFocus,
    focusedIdx,
    reset,
    name,
  }) => {
    const t = useTranslations();
    const [
      templateFormElementModalOpen,
      setTemplateFormElementModalOpen,
    ] = useState({ open: false, insertIndex: -1 });

    const [showSection, setShowSection] = useState(true);

    const { fields, append, insert } = useFieldArray<
      TemplateQuestionFormInput,
      "key"
    >({
      control,
      name: `${name}.templateQuestionsAttributes`,
      keyName: "key",
    });

    const _onRemove = useCallback(() => {
      if (confirm(t("templates.deleteSectionWarning") as string)) {
        onRemove(index);
      }
    }, [index, onRemove]);

    const _onRemoveQuestionWithoutSection = useCallback(() => {
      onRemove(index);
    }, [index, onRemove]);

    const handleRemoveQuestion = useCallback(
      (questionIndex) => {
        // clone it to make mutating the value easier
        const values = cloneDeep(getValues());

        get(values, name).templateQuestionsAttributes.splice(questionIndex, 1);
        reset(values);
      },
      [name]
    );

    const ModalComponent = (
      <TemplateFormElementModal
        insideSection
        open={templateFormElementModalOpen.open}
        onClose={() =>
          setTemplateFormElementModalOpen({ open: false, insertIndex: -1 })
        }
        setValue={setValue}
        getValues={getValues}
        sectionIndex={index}
        handleFocus={handleFocus}
        fields={fields}
        append={append}
        insert={insert}
        reset={reset}
        insertIndex={templateFormElementModalOpen.insertIndex}
        name={name}
      />
    );

    const addElementButton = useMemo(
      () => (
        <Button
          id={`${name}.addElementButton`}
          onClick={() =>
            setTemplateFormElementModalOpen({ open: true, insertIndex: -1 })
          }
        >
          {t("templates.createElementInsideSection")}
        </Button>
      ),
      [name]
    );

    const handleQuestionFocus = useCallback(
      (questionIdx: number) => {
        handleFocus(index, questionIdx);
      },
      [handleFocus, index]
    );

    if (defaultValue.type === "TemplateSectionBlank")
      return (
        <>
          {fields.map((field, questionIndex) => {
            const focused = focusedIdx === `${index}-${questionIndex}`;

            return (
              <div
                key={field.key}
                role={!focused ? "button" : undefined}
                onClick={() => handleQuestionFocus(questionIndex)}
                className={`mt-6`}
              >
                <input
                  type="hidden"
                  name={`${name}.type`}
                  ref={register()}
                  defaultValue={defaultValue.type}
                />
                <TemplateQuestionForm
                  index={questionIndex}
                  control={control}
                  register={register}
                  unregister={unregister}
                  setValue={setValue}
                  getValues={getValues}
                  reset={reset}
                  defaultValue={field}
                  shouldSelectReferent={shouldSelectReferent}
                  errors={errors?.templateQuestionsAttributes?.[questionIndex]}
                  onRemove={_onRemoveQuestionWithoutSection}
                  focused={focused}
                  name={`${name}.templateQuestionsAttributes[${questionIndex}]`}
                />
              </div>
            );
          })}
          {ModalComponent}
        </>
      );

    return (
      <div
        id={`${name}.sectionBlock`}
        className={`rounded shadow-md bg-light-0 ${
          index === 0 ? "mt-3" : "mt-6"
        }`}
      >
        <div className="flex items-center justify-between w-full px-4 py-3 rounded-t shadow-md bg-secondary-600">
          <span
            className="text-lg font-semibold text-white"
            id={`${name}.sectionHeader`}
          >
            {t("mads.sectionHeader", {
              sectionIndex: sectionDisplayIndex + 1,
              questionsCount: fields.length,
            })}
          </span>
          <Button
            id={`${name}.deleteSection`}
            onClick={() => _onRemove()}
            variant="subtle"
            trailingIcon={DeleteIcon}
          >
            {t("templates.deleteSection")}
          </Button>
        </div>
        <div className="px-6 py-6">
          <div className="flex justify-between pb-4">
            <input
              type="hidden"
              name={`${name}.type`}
              ref={register()}
              defaultValue={defaultValue.type}
            />
            <div className="flex-grow pr-8">
              <Input
                id={`${name}.title`}
                label={t("templates.sectionTitleLabel") as string}
                placeholder={t("templates.sectionTitlePlaceholder") as string}
                name={`${name}.title`}
                ref={register({
                  required: t("templates.sectionTitleRequired") as string,
                })}
                error={errors?.title}
                defaultValue={defaultValue.title}
              />
            </div>
          </div>
          <div className="pb-4">
            <Textarea
              id={`${name}.description`}
              label={t("templates.sectionDescriptionLabel") as string}
              placeholder={
                t("templates.sectionDescriptionPlaceholder") as string
              }
              name={`${name}.description`}
              ref={register()}
              rows={2}
              defaultValue={defaultValue.description}
            />
          </div>
          {showSection ? (
            <>
              <div className="flex items-center">
                <Button
                  variant="link"
                  scale="inline"
                  className="mt-0.5 text-sm"
                  onClick={() => setShowSection(!showSection)}
                >
                  {t("templates.hideContent")}
                </Button>
              </div>
              {fields.length === 0 ? (
                <div className="flex justify-center py-6">
                  {addElementButton}
                </div>
              ) : (
                <>
                  {fields.map((field, questionIndex) => {
                    const focused = focusedIdx === `${index}-${questionIndex}`;
                    return (
                      <React.Fragment key={questionIndex}>
                        <div className="relative flex justify-center sm:mx-8 group">
                          <div className="absolute w-full h-px inset-y-1/2 bg-light-100 group-hover:bg-light-500" />
                          <div className="">
                            <Button
                              id={`insert_question_${name}_${questionIndex}`}
                              icon={PlusIcon}
                              variant="subtle"
                              scale="xs"
                              onClick={() =>
                                setTemplateFormElementModalOpen({
                                  open: true,
                                  insertIndex: questionIndex,
                                })
                              }
                            />
                          </div>
                        </div>
                        <div
                          key={field.key}
                          role={!focused ? "button" : undefined}
                          className={`py-3`}
                          id={`${name}.questionBar`}
                          onClick={() => handleQuestionFocus(questionIndex)}
                        >
                          <TemplateQuestionForm
                            index={questionIndex}
                            control={control}
                            register={register}
                            unregister={unregister}
                            setValue={setValue}
                            getValues={getValues}
                            reset={reset}
                            defaultValue={field}
                            errors={
                              errors?.templateQuestionsAttributes?.[
                                questionIndex
                              ]
                            }
                            shouldSelectReferent={shouldSelectReferent}
                            onRemove={handleRemoveQuestion}
                            focused={focused}
                            name={`${name}.templateQuestionsAttributes[${questionIndex}]`}
                          />
                        </div>
                      </React.Fragment>
                    );
                  })}
                  <div className="flex flex-col items-center pt-6">
                    {addElementButton}
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              <div className="flex items-center pt-0.5">
                <Button
                  variant="link"
                  scale="inline"
                  className="text-sm"
                  onClick={() => setShowSection(!showSection)}
                >
                  {t("templates.showContent")}
                </Button>
              </div>
            </>
          )}
          {ModalComponent}
        </div>
      </div>
    );
  }
);
